import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface TourStep {
    /** Stable id selector to highlight. The Tour component looks up [data-tour-id="<id>"]. */
    anchorId: string;
    title: string;
    text: string;
    /** Path the tour wants the user on for this step. */
    path: string;
}

interface TourContextValue {
    active: boolean;
    step: number;
    steps: TourStep[];
    start: () => void;
    end: () => void;
    next: () => void;
    prev: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

const STEPS: TourStep[] = [
    {
        anchorId: "negotiationsTable",
        title: "One queue for every deal",
        text: "Tail-spend negotiation runs autonomously across 9,000+ suppliers. This table is your whole view. The segmented control filters it down — start with what needs you, then look at what's running on its own.",
        path: "/superadmin/demo/tail-spend/negotiations",
    },
    {
        anchorId: "dealThread",
        title: "The full story, in one stream",
        text: "Every deal opens to this two-panel view. The left side is the chronological feed — detection, classification, every email in and out, the agent's analysis, and any escalation. When the agent paused for you, you reply right here.",
        path: "/superadmin/demo/tail-spend/negotiations/deal-crescent",
    },
    {
        anchorId: "dealAutonomy",
        title: "The sidebar — autonomy, levers, compliance",
        text: "Right-hand sidebar tells you why the agent is doing what it's doing. The Autonomy box shows where this deal sits against the category policy (autonomous, e-sign, or strategic), and the pill in the header is also a Pause button — click it any time to stop the agent from acting until you resume. Below: the levers the agent can move, the market benchmark, and the required clauses for this category.",
        path: "/superadmin/demo/tail-spend/negotiations/deal-crescent",
    },
    {
        anchorId: "reportingPage",
        title: "Reporting — what the agents delivered",
        text: "Portfolio view: total value captured, cycle time, coverage across categories, and outcome rates by month. This is where you check that the program is actually moving the number, not just running.",
        path: "/superadmin/demo/tail-spend/reporting",
    },
    {
        anchorId: "guardrailsPage",
        title: "Guardrails — set the rules once",
        text: "Per category, set the limits the agent must respect: autonomous scope, e-sign band, walk-away floor, required clauses, and the concession ladder. Every deal is anchored to one of these policies, and the sidebar's Autonomy box links directly back here. Edits create a new version with a start date; the previous version is archived.",
        path: "/superadmin/demo/tail-spend/guardrails",
    },
];

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [active, setActive] = useState(false);
    const [step, setStep] = useState(0);

    const goTo = useCallback(
        (idx: number) => {
            const next = STEPS[idx];
            if (!next) return;
            setStep(idx);
            if (location.pathname !== next.path) {
                navigate(next.path);
            }
        },
        [location.pathname, navigate]
    );

    const start = useCallback(() => {
        setActive(true);
        goTo(0);
    }, [goTo]);

    const end = useCallback(() => {
        setActive(false);
    }, []);

    const next = useCallback(() => {
        if (step >= STEPS.length - 1) {
            end();
            return;
        }
        goTo(step + 1);
    }, [step, goTo, end]);

    const prev = useCallback(() => {
        if (step > 0) goTo(step - 1);
    }, [step, goTo]);

    const value = useMemo<TourContextValue>(
        () => ({
            active,
            step,
            steps: STEPS,
            start,
            end,
            next,
            prev,
        }),
        [active, step, start, end, next, prev]
    );

    return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export function useTour(): TourContextValue {
    const ctx = useContext(TourContext);
    if (!ctx) throw new Error("useTour must be used inside <TourProvider>");
    return ctx;
}
